import { useCallback, useEffect, useRef } from 'react';

import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import {
  closeGettingStarted,
  openGettingStarted,
  selectGettingStartedOpen,
} from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetGettingStartedModalStateQuery,
  useMarkGettingStartedModalSeenMutation,
} from '@backrs_ui/grow-ui/src/redux/oneTimeModal/oneTimeModal.api';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

export const useGettingStartedModal = ({
  enabled,
  autoShow = true,
}: {
  enabled: boolean;
  autoShow?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectGettingStartedOpen);
  const autoShownRef = useRef(false);
  const { isGuest } = useAuth();

  const { data: state } = useGetGettingStartedModalStateQuery(undefined, {
    skip: !enabled || isGuest,
  });
  const [markSeen] = useMarkGettingStartedModalSeenMutation();

  useEffect(() => {
    if (autoShownRef.current || !autoShow) return;
    if (isGuest) return;
    if (state && !state.seen) {
      autoShownRef.current = true;
      dispatch(openGettingStarted());
    }
  }, [state, isGuest, autoShow, dispatch]);

  const onOpen = useCallback(() => dispatch(openGettingStarted()), [dispatch]);

  const onAcknowledge = useCallback(() => {
    if (isGuest) return;
    if (state && !state.seen) {
      markSeen(undefined);
    }
  }, [state, markSeen, isGuest]);

  const onClose = useCallback(() => {
    dispatch(closeGettingStarted());
    // Nothing in the sandbox list navigates, so closing is the only chance to mark it seen.
    if (IS_SANDBOX) {
      onAcknowledge();
    }
  }, [dispatch, onAcknowledge]);

  return { open, onOpen, onClose, onAcknowledge };
};
